import { sectionStatusHandler, text } from "@theme/fetch";
import { morph, parseHtml, getRefs } from "@theme/helpers";
import { HTMLCustomElement } from "@theme/html-custom-element";
import { Swiper } from "@theme/swiper";

/**
 * Handles filter and sort selections with the Section Rendering API.
 */
class ResultsList extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on("click", this, this.handleResultsListClick.bind(this));
    this.initCollectionPromoSwipers();
    this.observeLoadMore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.loadMoreObserver?.disconnect();
  }

  /**
   * Drawer actions use `data-ref="filter-form.*"` (not `results-list.*`), so handle them here.
   * Filters and sort apply only when the user clicks Apply (not on each change).
   * @param {MouseEvent} event
   */
  handleResultsListClick(event) {
    const apply = event.target.closest('[data-ref="filter-form.apply"]');

    if (apply) {
      event.preventDefault();
      void this.fetchSectionFromForms();
      return;
    }

    const clear = event.target.closest('[data-ref="filter-form.clear"]');

    if (clear) {
      event.preventDefault();
      void this.clearFiltersAndRefresh();
      return;
    }

    this.handleLoadMoreClick(event);
  }

  /**
   * Clears facet checkboxes and price inputs, resets sort to the theme default, then reloads results.
   */
  async clearFiltersAndRefresh() {
    const filtersForm = this.querySelector('[data-ref="results-list.filters"]');

    if (filtersForm) {
      for (const input of filtersForm.querySelectorAll(
        'input[type="checkbox"]',
      )) {
        input.checked = false;
      }

      for (const input of filtersForm.querySelectorAll(
        'input[type="number"]',
      )) {
        const range = input.closest("price-range");

        if (range && typeof range.reset === "function") {
          continue;
        }

        input.value = "";
      }

      filtersForm.querySelector("price-range")?.reset?.();
    }

    const sortForm = this.querySelector('[data-ref="results-list.sort"]');
    const defaultSort = this.getAttribute("default-sort-by");

    if (sortForm && defaultSort) {
      for (const radio of sortForm.querySelectorAll(
        'input[type="radio"][name="sort_by"]',
      )) {
        radio.checked = radio.value === defaultSort;
      }
    }

    await this.fetchSectionFromForms();
  }

  /**
   * Reads filter and sort forms, updates the URL, and morphs section HTML from the Section Rendering API.
   */
  async fetchSectionFromForms() {
    const filtersForm = this.querySelector('[data-ref="results-list.filters"]');
    const sortForm = this.querySelector('[data-ref="results-list.sort"]');
    const data = [
      ...(filtersForm ? new FormData(filtersForm) : []),
      ...(sortForm ? new FormData(sortForm) : []),
    ];
    const params = this.filterSearchParams(new URLSearchParams(data));
    const section = this.sectionId
      ? ["section_id", this.sectionId]
      : [null, null];
    const query = new URLSearchParams([
      ...params.entries(),
      section,
    ]).toString();
    const { origin, pathname } = window.location;

    window.history.pushState(
      {},
      "",
      `${origin}${pathname}${params.size === 0 ? "" : `?${params.toString()}`}`,
    );

    try {
      await text(
        fetch(`${origin}${pathname}?${query}`),
        sectionStatusHandler,
      ).then(async (response) => {
        const update = parseHtml(response, this.tagName.toLowerCase());
        const refs = getRefs(update);

        /**
         * Manually replacing the results grid due to morphing issues with the product cards and Swiper.
         */
        await morph(this, update, undefined, (from) => !from.matches('[data-ref="results-list.grid"]'));

        this.refs.grid.innerHTML = refs.grid.innerHTML;

        this.resetRefs();
        this.initCollectionPromoSwipers({ reset: true });
        this.observeLoadMore();
        /**
         * Morph syncs markup from the server and drops client-only `is-open` without
         * running toggle-element close(); finish teardown so guards and scroll lock reset.
         */
        await this.querySelector("#filter-form")?.close?.();
      });
    } catch (error) {
      console.error(error);
      // window.location.reload();
    }
  }

  /**
   * Observes the load more element and auto-triggers when it enters the viewport.
   */
  observeLoadMore() {
    const loadMoreEl = this.querySelector(".collection__load-more");

    if (!loadMoreEl) return;

    this.loadMoreObserver?.disconnect();

    this.loadMoreObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        this.loadMoreObserver.disconnect();
        this.triggerLoadMore(
          loadMoreEl.querySelector(".collection__load-more-button"),
        );
      },
      { rootMargin: "0px 0px 1000px 0px" },
    );

    this.loadMoreObserver.observe(loadMoreEl);
  }

  /**
   * Handles a manual click on the load more button.
   * @param {MouseEvent} event
   */
  handleLoadMoreClick(event) {
    const button = event.target.closest(".collection__load-more-button");

    if (!button) return;

    event.preventDefault();
    this.triggerLoadMore(button);
  }

  /**
   * Fetches the next page and appends its product items to the grid.
   * @param {HTMLButtonElement} button
   */
  async triggerLoadMore(button) {
    if (!button || button.disabled) return;

    const nextUrl = button.dataset.nextUrl;

    if (!nextUrl) return;

    button.disabled = true;

    this.resetRefs();
    const grid =
      this.refs.grid ?? this.querySelector('[data-ref="results-list.grid"]');

    if (!grid) {
      button.disabled = false;
      return;
    }

    const url = this.buildLoadMoreSectionUrl(nextUrl);

    try {
      const response = await text(fetch(url.toString()), sectionStatusHandler);

      const parsed = parseHtml(response, "results-list");

      if (!parsed) {
        button.disabled = false;
        this.observeLoadMore();
        return;
      }

      const newItems = parsed.querySelectorAll(".collection__grid > li");

      newItems.forEach((item) => grid.appendChild(item));

      const newLoadMore = parsed.querySelector(".collection__load-more");
      const currentLoadMore = this.querySelector(".collection__load-more");

      if (newLoadMore) {
        currentLoadMore.replaceWith(newLoadMore);
      } else if (currentLoadMore) {
        currentLoadMore.remove();
      }

      this.initCollectionPromoSwipers();
      this.observeLoadMore();
    } catch {
      button.disabled = false;
      this.observeLoadMore();
    }
  }

  /**
   * Section request for the next page: keep `page` from `nextUrl`, merge facet/sort params from
   * the current address when missing (covers edge cases after client-side filter updates).
   * @param {string} nextUrl - `paginate.next.url` from Liquid.
   * @returns {URL}
   */
  buildLoadMoreSectionUrl(nextUrl) {
    const url = new URL(nextUrl, window.location.origin);
    const current = new URL(window.location.href);

    for (const key of new Set(current.searchParams.keys())) {
      if (key === "page" || key === "section_id") continue;
      if (url.searchParams.has(key)) continue;
      for (const value of current.searchParams.getAll(key)) {
        url.searchParams.append(key, value);
      }
    }

    if (this.sectionId) {
      url.searchParams.set("section_id", this.sectionId);
    }

    return url;
  }

  /**
   * Swiper for collection promo tiles (`data-ref="collection-promo.swiper"` in main-collection).
   * @param {{ reset?: boolean }} [options] - When true (after morph), tear down existing instances first.
   */
  initCollectionPromoSwipers(options = {}) {
    const elements = this.querySelectorAll(
      '[data-ref="collection-promo.swiper"]',
    );

    if (options.reset) {
      for (const el of elements) {
        el.swiper?.destroy(true, true);
      }
    }

    for (const el of elements) {
      if (el.swiper) continue;

      if (el.querySelectorAll(".swiper-slide").length < 2) continue;

      new Swiper(el, {
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        pagination: {
          el: el.querySelector(".swiper-pagination"),
          clickable: true,
        },
        autoplay: {
          delay: 3000,
          disableOnInteraction: true,
          pauseOnMouseEnter: true,
        },
        speed: 1000,
        loop: true,
        on: {
          autoplayTimeLeft(swiper, _time, progress) {
            const activeBullet = swiper.pagination?.el?.querySelector(
              ".swiper-pagination-bullet-active",
            );
            activeBullet?.style.setProperty("--promo-progress", 1 - progress);
          },
          slideChangeTransitionStart(swiper) {
            for (const bullet of swiper.pagination?.bullets ?? []) {
              bullet.style.setProperty("--promo-progress", "0");
            }
          },
        },
      });
    }
  }

  /**
   * Filters out empty values, or the default sort by option.
   * @param {URLSearchParams} params - The search params.
   * @returns {URLSearchParams}
   */
  filterSearchParams(params) {
    return new URLSearchParams([
      ...params.entries().filter(([key, value]) => {
        if (value === "") {
          return false;
        }

        const input = this.querySelector(`[name="${CSS.escape(key)}"]`);
        if (input instanceof HTMLInputElement && input.closest("price-range")) {
          if (input.dataset.priceRange === "min-input" && value === input.min) {
            return false;
          }

          if (input.dataset.priceRange === "max-input" && value === input.max) {
            return false;
          }
        }

        if (key === "sort_by" && this.hasAttribute("default-sort-by")) {
          return !(value === this.getAttribute("default-sort-by"));
        }

        return true;
      }),
    ]);
  }

  /**
   * The section container.
   */
  get sectionId() {
    return this.getAttribute("section-id");
  }
}

if (!customElements.get("results-list"))
  customElements.define("results-list", ResultsList);
